import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import AddUdfIcon from '@material-ui/icons/PlaylistAdd';
import React from 'react';
import { guid } from 'rewire-common';
import { DataSignal, observable, property, root, watch } from 'rewire-core';
import { createColumn, IColumn, IGrid } from 'rewire-grid';
import { IActionMenuItem, IToggleMenuItem } from 'rewire-ui';
import { ISort, IUdf, UdfDocumentType } from '../DataTypes';
import { history } from '../RouterWithHistory';
import Routes from '../Routes';
import EditDialogModel, { TEditSaveFn } from '../schedule/EditDialogModel';
import { UdfsContainerModel } from '../schedule/UdfsModel';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import { notifications } from '../ui/Notifications';
import PrioritySortMenuModel from '../ui/PrioritySortMenuModel';
import { formatUdfValue } from '../utility/Formatters';
import MasterDetailModel, { IMasterDetail } from './MasterDetailModel';
import PagingModel from './PagingModel';

export interface ISettingsBase {
  id: string;
  title: string;
  level(): number;
}

export interface ISettings extends ISettingsBase, IMasterDetail {}
export interface IOtherField {
  label: string;
  icon?: string;
  get: (item: any) => any;
  set: (item: any, value: any) => void;
}

export interface ISettingsGroup extends ISettingsBase {
  expanded: DataSignal<boolean>;
  group: ISettingsBase[];
  addButton?: ISettingsAddButton;
}

export interface ISettingsOptionsMenu {
  title: string;
  menuItems: IActionMenuItem[];
}

export function isGroup(obj: ISettingsBase): obj is ISettingsGroup {
  return !!(obj as ISettingsGroup).group;
}

export const DefaultSchema = `
  id
  name
  code
  tags
  fields
`;

export function makeLink(settings: ISettingsBase) {
  return `/home/settings/${settings.title.toLowerCase().replace(/ /g, '_')}`;
}

export function useWatch(fn: () => void, action: () => void, runActionOnStart = false) {
  let _dispose: () => void;
  React.useEffect(() => {
    root((dispose) => {
      _dispose = dispose;
      watch(fn, action, undefined, runActionOnStart);
    });
    return _dispose;
  }, []);
}

// const If = ({condition, then, otherwise}: any) => (((typeof condition === 'function') && condition()) || condition) ? then() : (otherwise ? otherwise() : null);
export const If = ({ condition, then, otherwise }: any) =>
  (typeof condition === 'function' && condition()) || condition
    ? React.isValidElement(then)
      ? then
      : then()
    : otherwise
      ? React.isValidElement(otherwise)
        ? otherwise
        : otherwise()
      : null;

export type ISettingsView = React.ComponentType<{ settings: SettingsModel }>;
export type IItemsPanelView = React.ComponentType<{ settings: SettingsModel; refresh: () => void; classes: any }>;
export type IEditPanelView = React.ComponentType<{ settings: SettingsModel; classes?: any }>;
export type IEditPanelFormView = React.ComponentType<{ editor: EditDialogModel; settings: SettingsModel; classes?: any }>;
export type IEditPanelHeaderView = React.ComponentType<{ settings: SettingsModel; classes?: any }>;
export type IEditPanelFooterView = React.ComponentType<{ settings: SettingsModel; classes?: any }>;

export default class SettingsModel<TState = unknown> extends MasterDetailModel<TState> implements ISettings {
  id: string = guid();
  paging: PagingModel;
  SettingsView?: ISettingsView;
  ItemsPanelView?: IItemsPanelView;
  EditPanelView?: IEditPanelView;
  EditPanelFormView?: IEditPanelFormView;
  EditPanelHeaderView?: IEditPanelHeaderView;
  EditPanelFooterView?: IEditPanelFooterView;
  filterMenuItems?: IToggleMenuItem[];
  sortModel?: PrioritySortMenuModel;
  protected _settingsState: { selected: boolean; archived: string; isLoading: boolean };
  onSelectedItem: any;

  public constructor(
    public title: string,
    protected _documentType?: UdfDocumentType,
    editor?: EditDialogModel,
    public otherFields?: IOtherField[],
    public optionsMenu?: ISettingsOptionsMenu,
    protected _update?: TEditSaveFn,
    onSelectedItem?: any,
  ) {
    super();
    this.paging = new PagingModel(this._load.bind(this));
    this._settingsState = observable({
      selected: false,
      archived: 'none',
      isLoading: false,
    });

    if (editor) {
      this.editor = editor;
    }
    if (onSelectedItem) {
      this.onSelectedItem = onSelectedItem;
    }
  }

  getDefaultEditPanelHeaderOptionsItems() {
    const isItemSelected = !!this.selectedItem;
    const editor = !!this.udfEditor;
    const changes = isItemSelected && this.hasChanges;

    const optionsItems: IActionMenuItem[] = [
      {
        name: 'Udfs',
        title: 'User Defined Fields',
        icon: AddUdfIcon,
        onClick: this.udfEditor,
        closeOnClick: true,
        disabled: () => changes || !editor,
      },
      {
        name: `delete${this.title}`,
        title: `Delete ${this.title}`,
        icon: DeleteIcon,
        onClick: this.removeItem,
        closeOnClick: true,
        disabled: () => !this._canDeleteItem || !isItemSelected,
      },
    ];

    return optionsItems;
  }

  get access(): boolean {
    return true;
  }

  get _canAddItem(): boolean {
    return true;
  }

  get _canEditItem(): boolean {
    return true;
  }

  get _canDeleteItem(): boolean {
    return true;
  }

  get udfs(): IUdf[] {
    return this._documentType ? udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType(this._documentType) : [];
  }

  get selected() {
    return this._settingsState.selected;
  }

  set isLoading(value: boolean) {
    this._settingsState.isLoading = value;
  }

  get isLoading(): boolean {
    return this._settingsState.isLoading || this.paging.loading;
  }

  get hiddenGridColumns(): string[] {
    return [];
  }

  canAddItem(): boolean {
    return this._canAddItem && super.canAddItem();
  }

  async select(selectedItemId?: string | null): Promise<void> {
    if (!this._settingsState.selected) {
      await this.paging.load();
      this.activate();
      this._settingsState.selected = true;
    }
    this.updateTitle();
    if (!selectedItemId && !this.selectedItem) return;
    if (selectedItemId && this.selectedItem && this.selectedItem.id === selectedItemId) return;
    const selectedItem = this.state.items.find((f) => f.id === selectedItemId);
    super.selectItem(selectedItem);

    if (this.onSelectedItem) {
      this.onSelectedItem(selectedItem);
    }
  }

  get titleText() {
    if (!this.selectedItem) return this.title;
    return `${this.title} - ${this.selectedItem.name}`;
  }

  deselect(selectedOnly: boolean = false): void {
    if (selectedOnly) {
      this._settingsState.selected = false;
    } else {
      this.select();
    }
    this.deactivate();
  }

  selectItem(item: any) {
    if (item) {
      const url = `${Routes.Settings}/${this.title.toLowerCase().replace(/ /g, '_')}?selectedItem=${item.id}`;
      history.push(url);
    }
  }

  udfEditor = () => {
    const udfEditor = (this.editor as UdfsContainerModel)?.udfEditor;
    if (udfEditor) {
      udfEditor.open();
    }
    return false;
  };

  private _save = async (items: any) => {
    const errStr = `There was an error saving ${this.title.toLowerCase()}`;
    try {
      if (this._update) {
        const result = await this._update('id', items);
        if (result?.length) {
          notifications.success(`${this.title} saved successfully`);
        } else {
          notifications.error(errStr);
        }
      }
    } catch (err) {
      notifications.error(errStr);
    }
  };

  async submit(items: any) {
    await this._save(items);
  }

  async addCustomGridColumns(grid: IGrid) {
    grid.spliceColumns(3, grid.columns.length - 3);
    const columns: IColumn[] = [];
    if (this.otherFields) {
      for (const field of this.otherFields) {
        columns.push(
          createColumn(field.label, field.label, {
            type: 'text',
            width: '150px',
            align: 'left',
            canSort: false,
            readOnly: true,
            accessor: { getter: field.get, setter: field.set },
          }),
        );
      }
    }
    if (this.udfs) {
      for (const udf of this.udfs) {
        // if (udf.type === 'expression') continue;
        columns.push(
          createColumn(['fields', udf.name], udf.displayName, {
            type: 'text',
            width: '150px',
            align: 'left',
            canSort: false,
            readOnly: true,
            map: (v) => formatUdfValue(udf, v) || '',
          }),
        );
      }
    }
    grid.spliceColumns(3, grid.columns.length - 3, ...columns);
  }

  protected async _load(_filter?: { search?: string }, _sort?: ISort): Promise<{ cursor?: string; totalCount: number }> {
    return { totalCount: 0 };
  }

  level() {
    return 1;
  }
}

export interface SettingsCTOR {
  new (title: string): ISettings;
}
export function settings(title: string, settings: SettingsCTOR = SettingsModel): ISettings {
  return new settings(title);
}

export interface ISettingsAddButton {
  title: string;
  onClick: () => void;
  visible: () => boolean;
}
export function settingsGroup(
  title: string,
  settings: ISettingsBase[],
  expanded?: boolean,
  addButton?: ISettingsAddButton,
): ISettingsGroup {
  const group = {
    id: guid(),
    title,
    level: () => 1,
    expanded: expanded === undefined ? property(true) : property(expanded),
    group: settings,
    addButton,
  };
  const level = () => group.level() + 1;
  for (const m of settings) {
    m.level = level;
  }
  return group;
}
